/**
 * OrDevisLiesTab — Onglet "Devis liés" de la fiche OR.
 *
 * Cas d'usage : un client refuse une prestation puis change d'avis. On doit
 * pouvoir retrouver l'historique des devis de ce dossier (même véhicule,
 * même client) et les ré-ouvrir / renvoyer.
 *
 * Source de vérité : endpoint backend `/api/or/{oid}/devis-lies` qui agrège
 * /devis-all filtré par vehicule_id + client_id.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileText, ExternalLink, Calendar, CheckCircle2, XCircle, Clock, Wrench, Receipt } from "lucide-react";
import api from "@/lib/api";

const CYCLE_BADGE = {
  envoye:   { label: "Envoyé",   color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  accepte:  { label: "Accepté",  color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  refuse:   { label: "Refusé",   color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  expire:   { label: "Expiré",   color: "bg-orange-50 text-orange-700 border-orange-200", icon: Clock },
  converti: { label: "Converti", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Receipt },
};

function fmtChf(n) {
  return (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = String(s).slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

function DevisRow({ d, currentOrId }) {
  const navigate = useNavigate();
  const isOR = d.source === "or";
  const cyc = CYCLE_BADGE[d.cycle] || CYCLE_BADGE.envoye;
  const CycIcon = cyc.icon;
  const isCurrent = d.or_id === currentOrId;

  const handleOpen = () => {
    if (isOR) {
      navigate(`/atelier/or/${d.or_id}`);
    } else {
      // Devis Facturation → ouvre la page Facturation avec query param devis
      navigate(`/facturation?tab=devis&devis=${d.id}`);
    }
  };

  return (
    <Card
      className={`p-3 ${isCurrent ? "border-[#52a675] bg-emerald-50/30" : "border-gray-200 hover:border-[#52a675]"} hover:shadow-sm transition`}
      data-testid={`or-devis-lies-row-${d.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono font-semibold text-sm text-gray-900">{d.numero}</span>
            <Badge variant="outline" className={`${cyc.color} text-[10px]`}>
              <CycIcon className="w-3 h-3 mr-1" /> {cyc.label}
            </Badge>
            {isOR ? (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                <Wrench className="w-3 h-3 mr-1" /> Atelier · OR {d.or_numero}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-[10px]">
                <FileText className="w-3 h-3 mr-1" /> Direct
              </Badge>
            )}
            {isCurrent && (
              <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                Cet OR
              </Badge>
            )}
          </div>
          <div className="text-xs text-gray-600 truncate">
            {d.titre}{d.vehicule_label && <span className="text-gray-400"> · {d.vehicule_label}</span>}
          </div>
          <div className="text-[11px] text-gray-400 mt-0.5">
            {fmtDate(d.date_emission)}
            {d.date_validite && <> · validité {fmtDate(d.date_validite)}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-sm font-bold text-gray-900">
              {d.montant_ttc != null ? `${fmtChf(d.montant_ttc)} CHF` : "—"}
            </div>
          </div>
          {!isCurrent && (
            <Button size="sm" variant="outline" onClick={handleOpen} data-testid={`or-devis-lies-open-${d.id}`}>
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ouvrir
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}


export default function OrDevisLiesTab({ orId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/or/${orId}/devis-lies`).then(({ data }) => {
      if (!cancelled) setData(data);
    }).catch(() => {
      if (!cancelled) setData({ or_courant: null, memes_vehicule: [], memes_client: [] });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [orId]);

  if (loading) return <div className="text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!data) return null;

  const memeVeh = data.memes_vehicule || [];
  const memeCli = data.memes_client || [];

  const empty = !data.or_courant && memeVeh.length === 0 && memeCli.length === 0;
  if (empty) {
    return (
      <Card className="p-8 text-center text-gray-400 border-dashed border-gray-300" data-testid="or-devis-lies-empty">
        <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        Aucun devis lié à ce dossier (ni véhicule, ni client).
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="or-devis-lies-tab">
      {data.or_courant && (
        <section>
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Devis de cet OR
          </h4>
          <DevisRow d={data.or_courant} currentOrId={orId} />
        </section>
      )}

      {memeVeh.length > 0 && (
        <section>
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" />
            Autres devis sur ce véhicule ({memeVeh.length})
          </h4>
          <div className="space-y-2">
            {memeVeh.map((d) => <DevisRow key={`v-${d.source}-${d.id}`} d={d} currentOrId={orId} />)}
          </div>
        </section>
      )}

      {memeCli.length > 0 && (
        <section>
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
            Autres devis du même client ({memeCli.length})
          </h4>
          <div className="space-y-2">
            {memeCli.map((d) => <DevisRow key={`c-${d.source}-${d.id}`} d={d} currentOrId={orId} />)}
          </div>
        </section>
      )}
    </div>
  );
}
